import DOMPurify from "dompurify";

/**
 * The only HTML Oliver's chat may inject: what `marked` and MessageBubble produce, and
 * nothing a model's text could add — no scripts, handlers, inline styles, forms or frames.
 */
const CONFIG = {
  ALLOWED_TAGS: [
    "a",
    "b",
    "blockquote",
    "br",
    "button",
    "code",
    "del",
    "div",
    "em",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "hr",
    "i",
    "img",
    "li",
    "ol",
    "p",
    "pre",
    "s",
    "span",
    "strong",
    "sub",
    "sup",
    "table",
    "tbody",
    "td",
    "th",
    "thead",
    "tr",
    "ul",
  ],
  ALLOWED_ATTR: [
    "align",
    "alt",
    "aria-busy",
    "aria-label",
    "class",
    "data-artifact-retry",
    "data-citation",
    "data-oliver-internal",
    "data-sources",
    "decoding",
    "download",
    "href",
    "rel",
    "scope",
    "src",
    "start",
    "tabindex",
    "target",
    "title",
    "type",
  ],
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  // Plain text, never a URL: "Revenue: Q3" would otherwise fail the URI check.
  ADD_URI_SAFE_ATTR: ["aria-label", "download"],
};

/**
 * The class names MessageBubble itself renders. Anything else is dropped: with the app's
 * global CSS, a model-chosen class could turn a link into an invisible full-screen overlay.
 */
const OWN_CLASSES = new Set([
  "artifact-pending",
  "artifact-pending--failed",
  "artifact-retry",
  "artifact-skeleton",
  "chart-action-btn",
  "chart-actions",
  "chart-caption",
  "chart-expand-btn",
  "chart-image-wrapper",
  "citation-marker",
  "fa-book-open",
  "fa-circle-notch",
  "fa-download",
  "fa-exclamation-circle",
  "fa-expand",
  "fa-file-circle-exclamation",
  "fa-file-excel",
  "fa-file-pdf",
  "fa-file-powerpoint",
  "fa-file-word",
  "fa-spin",
  "fa-table",
  "fas",
  "file-card",
  "file-card--excel",
  "file-card--failed",
  "file-card--pdf",
  "file-card--powerpoint",
  "file-card--resolving",
  "file-card--word",
  "file-card__bar",
  "file-card__bar--details",
  "file-card__bar--name",
  "file-card__details",
  "file-card__download",
  "file-card__download-label",
  "file-card__footer",
  "file-card__icon",
  "file-card__main",
  "file-card__name",
  "file-card__text",
  "markdown-table-wrapper",
  "source-line",
  "source-line__text",
  "table-card",
  "table-card__actions",
  "table-card__cell--nowrap",
  "table-card__cell--number",
  "table-card__download",
  "table-card__expand",
  "table-card__footer",
  "table-card__scroll",
  "table-card__skeleton",
  "table-card__source",
  "table-card__meta",
  "table-card__title",
]);

/** `marked`'s fenced-code language class. */
const CODE_LANGUAGE_CLASS = /^language-[\w#+.-]+$/;

/** A path on this origin; `//host` and `/\host` are other origins. */
const SAME_ORIGIN_PATH = /^\/(?![/\\])/;

// Its own instance, so the hook never reaches another DOMPurify consumer.
const purify = DOMPurify();

purify.addHook("afterSanitizeAttributes", (node) => {
  if (node.hasAttribute("class")) {
    const own = [...node.classList].filter(
      (name) => OWN_CLASSES.has(name) || CODE_LANGUAGE_CLASS.test(name)
    );
    if (own.length) node.setAttribute("class", own.join(" "));
    else node.removeAttribute("class");
  }
  if (node.hasAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
  if (!SAME_ORIGIN_PATH.test(node.getAttribute("href") || "")) {
    node.removeAttribute("data-oliver-internal");
  }
});

/**
 * Sanitised HTML in which an image survives only if `isOwnImage(src)` says it is one of
 * Oliver's own files; any other becomes its alt text, chart actions and all. Done on the
 * inert parsed tree, so a refused image is never requested.
 */
export function sanitizeHtml(html, isOwnImage) {
  const body = purify.sanitize(html, { ...CONFIG, RETURN_DOM: true });
  for (const img of body.querySelectorAll("img")) {
    if (isOwnImage(img.getAttribute("src"))) continue;
    (img.closest(".chart-image-wrapper") || img).replaceWith(
      img.getAttribute("alt") || ""
    );
  }
  return body.innerHTML;
}
